'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GameParameters, detectFatigue, adaptParametersRealTime } from '@/lib/services/brainGymService';

// ─── Shared Types ─────────────────────────────────────────────────────────────

export interface GameResult {
  score: number;
  accuracy: number;
  reactionTimeAvg: number;
  trialsTotal: number;
  trialsCorrect: number;
  durationMs: number;
  difficultyAdjustments: number;
  fatigueDetected: boolean;
  reactionTimes: number[];
}

// ─── Go / No-Go Game ──────────────────────────────────────────────────────────

export function GoNoGoGame({
  params,
  onComplete,
}: {
  params: GameParameters;
  onComplete: (result: GameResult) => void;
}) {
  const [trial, setTrial] = useState(0);
  const [stimulus, setStimulus] = useState<'go' | 'nogo' | null>(null);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | 'miss' | null>(null);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [accuracyHistory, setAccuracyHistory] = useState<number[]>([]);
  const [results, setResults] = useState<{ correct: boolean; rt: number }[]>([]);
  const [diffAdjustments, setDiffAdjustments] = useState(0);
  const [currentParams, setCurrentParams] = useState(params);
  const [done, setDone] = useState(false);
  const [responded, setResponded] = useState(false);
  const stimTime = useRef(0);
  const startTime = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNextTrial = useCallback((trialIdx: number, p: GameParameters) => {
    if (trialIdx >= p.totalTrials) {
      setDone(true);
      return;
    }
    const isGo = Math.random() > 0.35; // 65% go, 35% no-go
    setStimulus(isGo ? 'go' : 'nogo');
    setFeedback(null);
    setResponded(false);
    stimTime.current = Date.now();

    // Auto-advance after display duration
    timerRef.current = setTimeout(() => {
      setStimulus(prev => {
        if (prev === 'go') {
          // Missed a go stimulus
          setFeedback('miss');
          setResults(r => [...r, { correct: false, rt: p.displayDurationMs }]);
          setReactionTimes(rt => [...rt, p.displayDurationMs]);
          setAccuracyHistory(a => [...a, 0]);
        }
        return null;
      });
      setTimeout(() => {
        setFeedback(null);
        setTrial(t => t + 1);
      }, 400);
    }, p.displayDurationMs);
  }, []);

  useEffect(() => {
    if (!done) {
      const p = currentParams;
      // Real-time adaptation
      if (trial > 0 && trial % 5 === 0 && reactionTimes.length >= 5) {
        const avgRt = reactionTimes.slice(-5).reduce((a, b) => a + b, 0) / 5;
        const recentAcc = accuracyHistory.slice(-5).reduce((a, b) => a + b, 0) / 5 * 100;
        const adapted = adaptParametersRealTime(p, recentAcc, avgRt, trial);
        if (adapted.stimulusIntervalMs !== p.stimulusIntervalMs) {
          setCurrentParams(adapted);
          setDiffAdjustments(d => d + 1);
        }
      }

      const delay = trial === 0 ? 500 : currentParams.stimulusIntervalMs;
      const t = setTimeout(() => showNextTrial(trial, currentParams), delay);
      return () => { clearTimeout(t); if (timerRef.current) clearTimeout(timerRef.current); };
    }
  }, [trial, done]);

  useEffect(() => {
    if (done && results.length > 0) {
      let correct = results.filter(r => r.correct).length;
      const accuracy = Math.round((correct / results.length) * 100);
      const avgRt = reactionTimes.length > 0
        ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)
        : 600;
      const rtScore = Math.max(0, Math.min(100, ((800 - avgRt) / 600) * 100));
      const score = Math.round(accuracy * 0.6 + rtScore * 0.4);
      const fatigue = detectFatigue(reactionTimes, accuracyHistory);

      onComplete({
        score: Math.min(100, score),
        accuracy,
        reactionTimeAvg: avgRt,
        trialsTotal: results.length,
        trialsCorrect: correct,
        durationMs: Date.now() - startTime.current,
        difficultyAdjustments: diffAdjustments,
        fatigueDetected: fatigue,
        reactionTimes,
      });
    }
  }, [done]);

  const handleResponse = () => {
    if (responded || done) return;
    if (!stimulus) return;
    setResponded(true);
    if (timerRef.current) clearTimeout(timerRef.current);

    const rt = Date.now() - stimTime.current;
    const isCorrect = stimulus === 'go';
    setFeedback(isCorrect ? 'correct' : 'wrong');
    if (isCorrect) setReactionTimes(prev => [...prev, rt]);
    setResults(prev => [...prev, { correct: isCorrect, rt }]);
    setAccuracyHistory(prev => [...prev, isCorrect ? 1 : 0]);

    setTimeout(() => {
      setStimulus(null);
      setFeedback(null);
      setTrial(t => t + 1);
    }, 450);
  };

  const progress = (trial / currentParams.totalTrials) * 100;
  const correctCount = results.filter(r => r.correct).length;

  return (
    <div className="flex flex-col items-center gap-5 select-none">
      {/* Instructions */}
      <div className="text-center space-y-1">
        <p className="text-[11px] text-[#8BAFD4]">
          Tap <span className="text-emerald-400 font-bold">GO</span> for green · 
          Hold for <span className="text-red-400 font-bold">red</span>
        </p>
        <p className="text-[10px] text-[#4A7AB5]">Trial {Math.min(trial + 1, currentParams.totalTrials)} of {currentParams.totalTrials}</p>
      </div>

      {/* Stimulus area */}
      <button
        onClick={handleResponse}
        className="relative flex items-center justify-center rounded-full transition-all duration-150 active:scale-95 focus:outline-none"
        style={{
          width: 'min(140px, 42vw)',
          height: 'min(140px, 42vw)',
          background: stimulus === 'go'
            ? feedback === 'correct' ? 'rgba(16,185,129,0.9)' : 'rgba(16,185,129,0.75)'
            : stimulus === 'nogo'
            ? feedback === 'wrong' ? 'rgba(239,68,68,0.9)' : 'rgba(239,68,68,0.75)'
            : 'rgba(30,58,95,0.25)',
          border: stimulus
            ? `3px solid ${stimulus === 'go' ? 'rgba(16,185,129,0.9)' : 'rgba(239,68,68,0.9)'}`
            : '2px solid rgba(30,58,95,0.4)',
          boxShadow: stimulus === 'go' ?'0 0 32px rgba(16,185,129,0.5)'
            : stimulus === 'nogo' ?'0 0 32px rgba(239,68,68,0.5)' :'none',
          cursor: stimulus === 'go' ? 'pointer' : 'default',
        }}
      >
        {feedback === 'correct' && <span className="text-white text-3xl font-bold">✓</span>}
        {feedback === 'wrong' && <span className="text-white text-3xl font-bold">✗</span>}
        {feedback === 'miss' && <span className="text-white text-2xl font-bold">!</span>}
        {!feedback && !stimulus && (
          <div className="w-3 h-3 rounded-full bg-[#1E3A5F]/60 animate-pulse" />
        )}
        {!feedback && stimulus && (
          <span className="text-white text-[13px] font-bold">
            {stimulus === 'go' ? 'TAP!' : 'HOLD'}
          </span>
        )}
      </button>

      {/* Stats row */}
      <div className="flex gap-4 text-center">
        <div>
          <p className="text-[18px] font-bold text-emerald-400">{correctCount}</p>
          <p className="text-[10px] text-[#4A7AB5]">Correct</p>
        </div>
        <div>
          <p className="text-[18px] font-bold text-[#F97316]">{results.length - correctCount}</p>
          <p className="text-[10px] text-[#4A7AB5]">Errors</p>
        </div>
        <div>
          <p className="text-[18px] font-bold text-[#38BDF8]">
            {reactionTimes.length > 0
              ? `${Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)}ms`
              : '—'}
          </p>
          <p className="text-[10px] text-[#4A7AB5]">Avg RT</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full max-w-[240px]">
        <div className="h-1.5 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #10B981, #34D399)' }}
          />
        </div>
      </div>

      {diffAdjustments > 0 && (
        <p className="text-[10px] text-[#F97316]/70">⚡ Difficulty adapted {diffAdjustments}×</p>
      )}
    </div>
  );
}

// ─── N-Back Game ──────────────────────────────────────────────────────────────

export function NBackGame({
  params,
  onComplete,
}: {
  params: GameParameters;
  onComplete: (result: GameResult) => void;
}) {
  const n = params.nBackLevel;
  const GRID = 3;
  const [sequence, setSequence] = useState<number[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [activeCell, setActiveCell] = useState<number | null>(null);
  const [userResponses, setUserResponses] = useState<Record<number, boolean>>({});
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [accuracyHistory, setAccuracyHistory] = useState<number[]>([]);
  const [diffAdjustments, setDiffAdjustments] = useState(0);
  const [currentParams, setCurrentParams] = useState(params);
  const [done, setDone] = useState(false);
  const stimTime = useRef(0);
  const startTime = useRef(Date.now());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const seq = Array.from({ length: currentParams.totalTrials }, () =>
      Math.floor(Math.random() * GRID * GRID)
    );
    setSequence(seq);
    let idx = 0;

    const tick = () => {
      if (idx >= seq.length) {
        setDone(true);
        setActiveCell(null);
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }
      setCurrentIdx(idx);
      setActiveCell(seq[idx]);
      setFeedback(null);
      stimTime.current = Date.now();
      idx++;
    };

    tick();
    intervalRef.current = setInterval(tick, currentParams.stimulusIntervalMs);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  useEffect(() => {
    if (done && sequence.length > 0) {
      let correct = 0;
      let total = 0;
      const rts = reactionTimes;

      for (let i = n; i < sequence.length; i++) {
        const isMatch = sequence[i] === sequence[i - n];
        if (isMatch) {
          total++;
          if (userResponses[i]) correct++;
        } else if (userResponses[i]) {
          correct = Math.max(0, correct - 1);
        }
      }

      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 50;
      const avgRt = rts.length > 0 ? Math.round(rts.reduce((a, b) => a + b, 0) / rts.length) : 800;
      const rtScore = Math.max(0, Math.min(100, ((2000 - avgRt) / 1800) * 100));
      const score = Math.round(accuracy * 0.7 + rtScore * 0.3);
      const fatigue = detectFatigue(rts, accuracyHistory);

      onComplete({
        score: Math.min(100, score),
        accuracy,
        reactionTimeAvg: avgRt,
        trialsTotal: sequence.length,
        trialsCorrect: correct,
        durationMs: Date.now() - startTime.current,
        difficultyAdjustments: diffAdjustments,
        fatigueDetected: fatigue,
        reactionTimes: rts,
      });
    }
  }, [done]);

  const handleMatch = () => {
    if (currentIdx < n || done) return;
    const rt = Date.now() - stimTime.current;
    const isActualMatch = sequence[currentIdx] === sequence[currentIdx - n];
    setFeedback(isActualMatch ? 'correct' : 'wrong');
    setReactionTimes(prev => [...prev, rt]);
    setAccuracyHistory(prev => [...prev, isActualMatch ? 1 : 0]);
    setUserResponses(prev => ({ ...prev, [currentIdx]: true }));
    setTimeout(() => setFeedback(null), 400);
  };

  const progress = sequence.length > 0 ? ((currentIdx + 1) / sequence.length) * 100 : 0;

  return (
    <div className="flex flex-col items-center gap-5 select-none">
      <div className="text-center space-y-1">
        <p className="text-[11px] text-[#8BAFD4]">
          Press <span className="text-[#8B5CF6] font-bold">MATCH</span> when position matches {n} step{n > 1 ? 's' : ''} ago
        </p>
        <p className="text-[10px] text-[#4A7AB5]">N = {n} · Trial {Math.min(currentIdx + 1, sequence.length)} of {sequence.length}</p>
      </div>

      {/* Grid */}
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${GRID}, 1fr)`, width: 'min(168px, 52vw)' }}
      >
        {Array.from({ length: GRID * GRID }, (_, i) => (
          <div
            key={i}
            className="rounded-xl transition-all duration-100"
            style={{
              width: 'min(50px, 15vw)',
              height: 'min(50px, 15vw)',
              background: activeCell === i
                ? feedback === 'correct' ? 'rgba(16,185,129,0.85)'
                : feedback === 'wrong' ? 'rgba(239,68,68,0.85)'
                : 'rgba(139,92,246,0.8)' :'rgba(30,58,95,0.25)',
              border: activeCell === i
                ? `2px solid ${feedback === 'correct' ? 'rgba(16,185,129,1)' : feedback === 'wrong' ? 'rgba(239,68,68,1)' : 'rgba(139,92,246,0.9)'}`
                : '1px solid rgba(30,58,95,0.4)',
              boxShadow: activeCell === i ? `0 0 16px ${feedback === 'correct' ? 'rgba(16,185,129,0.5)' : feedback === 'wrong' ? 'rgba(239,68,68,0.5)' : 'rgba(139,92,246,0.5)'}` : 'none',
            }}
          />
        ))}
      </div>

      {/* Match button */}
      <button
        onClick={handleMatch}
        disabled={currentIdx < n || done}
        className="px-10 py-3.5 rounded-2xl font-bold text-[14px] text-white transition-all active:scale-95 min-h-[52px]"
        style={{
          background: currentIdx < n || done ? 'rgba(139,92,246,0.3)' : 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
          boxShadow: currentIdx < n || done ? 'none' : '0 4px 20px rgba(139,92,246,0.4)',
          opacity: currentIdx < n || done ? 0.5 : 1,
        }}
      >
        ✓ MATCH
      </button>

      {/* Progress */}
      <div className="w-full max-w-[240px]">
        <div className="h-1.5 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #8B5CF6, #A78BFA)' }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Task Switching Game ──────────────────────────────────────────────────────

const SWITCH_RULES = [
  { label: 'Is it > 5?', check: (n: number) => n > 5, color: '#F97316' },
  { label: 'Is it even?', check: (n: number) => n % 2 === 0, color: '#38BDF8' },
  { label: 'Is it < 5?', check: (n: number) => n < 5, color: '#10B981' },
];

export function TaskSwitchingGame({
  params,
  onComplete,
}: {
  params: GameParameters;
  onComplete: (result: GameResult) => void;
}) {
  const [trial, setTrial] = useState(0);
  const [ruleIdx, setRuleIdx] = useState(0);
  const [number, setNumber] = useState(0);
  const [results, setResults] = useState<{ correct: boolean; rt: number; isSwitch: boolean }[]>([]);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [accuracyHistory, setAccuracyHistory] = useState<number[]>([]);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [diffAdjustments, setDiffAdjustments] = useState(0);
  const [currentParams, setCurrentParams] = useState(params);
  const [done, setDone] = useState(false);
  const [prevRuleIdx, setPrevRuleIdx] = useState(-1);
  const stimTime = useRef(0);
  const startTime = useRef(Date.now());

  const showNext = useCallback((trialIdx: number, p: GameParameters) => {
    if (trialIdx >= p.totalTrials) { setDone(true); return; }

    // Switch rule every ruleComplexity trials
    const switchInterval = Math.max(2, 4 - p.ruleComplexity);
    const newRuleIdx = trialIdx % (switchInterval * SWITCH_RULES.length) < switchInterval
      ? Math.floor(trialIdx / switchInterval) % SWITCH_RULES.length
      : ruleIdx;

    setPrevRuleIdx(ruleIdx);
    setRuleIdx(newRuleIdx);
    setNumber(1 + Math.floor(Math.random() * 9));
    stimTime.current = Date.now();
  }, [ruleIdx]);

  useEffect(() => {
    const t = setTimeout(() => showNext(trial, currentParams), trial === 0 ? 300 : 400);
    return () => clearTimeout(t);
  }, [trial]);

  useEffect(() => {
    if (done && results.length > 0) {
      let correct = results.filter(r => r.correct).length;
      const accuracy = Math.round((correct / results.length) * 100);
      const avgRt = reactionTimes.length > 0
        ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)
        : 900;
      const rtScore = Math.max(0, Math.min(100, ((1500 - avgRt) / 1300) * 100));
      const score = Math.round(accuracy * 0.65 + rtScore * 0.35);
      const fatigue = detectFatigue(reactionTimes, accuracyHistory);

      onComplete({
        score: Math.min(100, score),
        accuracy,
        reactionTimeAvg: avgRt,
        trialsTotal: results.length,
        trialsCorrect: correct,
        durationMs: Date.now() - startTime.current,
        difficultyAdjustments: diffAdjustments,
        fatigueDetected: fatigue,
        reactionTimes,
      });
    }
  }, [done]);

  const respond = (answer: boolean) => {
    if (done || feedback) return;
    const rt = Date.now() - stimTime.current;
    let correct = answer === SWITCH_RULES[ruleIdx].check(number);
    const isSwitch = ruleIdx !== prevRuleIdx;
    setFeedback(correct ? 'correct' : 'wrong');
    setResults(prev => [...prev, { correct, rt, isSwitch }]);
    setReactionTimes(prev => [...prev, rt]);
    setAccuracyHistory(prev => [...prev, correct ? 1 : 0]);

    // Real-time adaptation every 6 trials
    if ((trial + 1) % 6 === 0 && reactionTimes.length >= 5) {
      const avgRt = reactionTimes.slice(-5).reduce((a, b) => a + b, 0) / 5;
      const recentAcc = accuracyHistory.slice(-5).reduce((a, b) => a + b, 0) / 5 * 100;
      const adapted = adaptParametersRealTime(currentParams, recentAcc, avgRt, trial);
      if (adapted.stimulusIntervalMs !== currentParams.stimulusIntervalMs) {
        setCurrentParams(adapted);
        setDiffAdjustments(d => d + 1);
      }
    }

    setTimeout(() => {
      setFeedback(null);
      setTrial(t => t + 1);
    }, 380);
  };

  const rule = SWITCH_RULES[ruleIdx];
  const isSwitch = ruleIdx !== prevRuleIdx && trial > 0;
  const progress = (trial / currentParams.totalTrials) * 100;

  return (
    <div className="flex flex-col items-center gap-5 select-none">
      {/* Rule indicator */}
      <div className="flex items-center gap-2">
        <div
          className="px-4 py-2 rounded-xl text-[12px] font-bold transition-all duration-200"
          style={{
            background: `${rule.color}18`,
            border: `1px solid ${rule.color}40`,
            color: rule.color,
          }}
        >
          {rule.label}
        </div>
        {isSwitch && (
          <span className="text-[10px] text-[#F97316] font-bold animate-pulse">SWITCH!</span>
        )}
      </div>

      {/* Number stimulus */}
      <div
        className="flex items-center justify-center rounded-2xl transition-all duration-150"
        style={{
          width: 'min(110px, 34vw)',
          height: 'min(110px, 34vw)',
          background: 'rgba(6,13,26,0.8)',
          border: `2px solid ${feedback === 'correct' ? 'rgba(16,185,129,0.7)' : feedback === 'wrong' ? 'rgba(239,68,68,0.7)' : 'rgba(30,58,95,0.5)'}`,
          boxShadow: feedback === 'correct' ? '0 0 20px rgba(16,185,129,0.3)' : feedback === 'wrong' ? '0 0 20px rgba(239,68,68,0.3)' : 'none',
        }}
      >
        <span
          className="text-[52px] font-bold transition-colors duration-150"
          style={{
            color: feedback === 'correct' ? '#10B981' : feedback === 'wrong' ? '#EF4444' : 'white',
          }}
        >
          {number}
        </span>
      </div>

      {/* Response buttons */}
      <div className="flex gap-4">
        <button
          onClick={() => respond(true)}
          disabled={!!feedback || done}
          className="px-8 py-3 rounded-xl font-bold text-[14px] text-white transition-all active:scale-95 min-h-[48px]"
          style={{
            background: 'linear-gradient(135deg, #10B981, #059669)',
            boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
            opacity: feedback ? 0.5 : 1,
          }}
        >
          YES
        </button>
        <button
          onClick={() => respond(false)}
          disabled={!!feedback || done}
          className="px-8 py-3 rounded-xl font-bold text-[14px] text-white transition-all active:scale-95 min-h-[48px]"
          style={{
            background: 'linear-gradient(135deg, #EF4444, #DC2626)',
            boxShadow: '0 4px 14px rgba(239,68,68,0.35)',
            opacity: feedback ? 0.5 : 1,
          }}
        >
          NO
        </button>
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-center">
        <div>
          <p className="text-[16px] font-bold text-emerald-400">{results.filter(r => r.correct).length}</p>
          <p className="text-[10px] text-[#4A7AB5]">Correct</p>
        </div>
        <div>
          <p className="text-[16px] font-bold text-[#F97316]">{trial}</p>
          <p className="text-[10px] text-[#4A7AB5]">Trial</p>
        </div>
        <div>
          <p className="text-[16px] font-bold text-[#38BDF8]">
            {reactionTimes.length > 0
              ? `${Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)}ms`
              : '—'}
          </p>
          <p className="text-[10px] text-[#4A7AB5]">Avg RT</p>
        </div>
      </div>

      {/* Progress */}
      <div className="w-full max-w-[240px]">
        <div className="h-1.5 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #10B981, #34D399)' }}
          />
        </div>
      </div>

      {diffAdjustments > 0 && (
        <p className="text-[10px] text-[#F97316]/70">⚡ Adapted {diffAdjustments}×</p>
      )}
    </div>
  );
}
